import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

const CartContext = createContext(null);
const KEY = "3dzona_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  const [toast, setToast] = useState(null); // { id, name, qty } | null
  const toastTimer = useRef(null);
  const toastSeq = useRef(0);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  // Each cart line is identified by (id + variant_color) so same product with different variants are separate
  const lineKey = (item) => `${item.id}::${item.variant_color || ""}`;

  const add = (product, qty = 1, variantColor = null) => {
    const newLine = {
      id: product.id, name: product.name, slug: product.slug,
      price: product.price, image_url: product.image_url, quantity: qty,
      variant_color: variantColor,
      category: product.category || null,
    };
    let lineTotal = qty;
    setItems((cur) => {
      const i = cur.findIndex((x) => lineKey(x) === lineKey(newLine));
      if (i >= 0) {
        const next = [...cur];
        lineTotal = next[i].quantity + qty;
        next[i] = { ...next[i], quantity: lineTotal };
        return next;
      }
      return [...cur, newLine];
    });
    // toastSeq unikalus id => naudojamas kaip React `key` VIDINIAM elementui, todel
    // spaudziant is eiles React KIEKVIENA karta perkuria DOM elementa is naujo
    // (ne tik atnaujina teksta), o tai reiskia CSS @keyframes animacija visada
    // startuoja is nulio, ne tik pirma karta.
    toastSeq.current += 1;
    setToast({ id: toastSeq.current, name: product.name, qty });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };
  const remove = (key) => setItems((cur) => cur.filter((x) => lineKey(x) !== key));
  const setQty = (key, qty) => setItems((cur) => cur.map((x) => lineKey(x) === key ? { ...x, quantity: Math.max(1, qty) } : x));
  const clear = () => setItems([]);
  const count = items.reduce((s, x) => s + x.quantity, 0);
  const total = items.reduce((s, x) => s + x.price * x.quantity, 0);

  return (
    <CartContext.Provider value={{ items, lineKey, add, remove, setQty, clear, count, total }}>
      {children}
      {toast && (
        // ISORINIS div: TIK pozicionavimas (fixed + centravimas mobile / prisegimas desineje
        // desktope) - sio elemento transform (centravimui) animacija NELIECIA.
        <div className="fixed z-[200] left-1/2 -translate-x-1/2 bottom-20 md:bottom-8 md:left-auto md:right-8 md:translate-x-0 max-w-[92vw] md:max-w-sm pointer-events-none">
          <style>{`
            @keyframes cartToastPop {
              0%   { opacity: 0; transform: translateY(14px) scale(0.55); }
              55%  { opacity: 1; transform: translateY(-6px) scale(1.08); }
              75%  { transform: translateY(2px) scale(0.98); }
              100% { opacity: 1; transform: translateY(0) scale(1); }
            }
            .cart-toast-pop { animation: cartToastPop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
          `}</style>
          {/* VIDINIS div: `key` priverčia React kiekviena karta ji perkurti is naujo,
              todel animacija visada persuka nuo pradzios, net spaudziant greitai is eiles. */}
          <div
            key={toast.id}
            className="cart-toast-pop pointer-events-auto bg-[#0d0d12] text-white border border-[#00B5E2]/60 shadow-2xl rounded-xl px-4 py-3.5 flex items-center gap-3"
            data-testid="cart-toast"
          >
            <div className="w-8 h-8 rounded-full bg-[#00C853] flex items-center justify-center shrink-0">
              <Check size={17} className="text-white" strokeWidth={3} />
            </div>
            <div className="text-sm min-w-0">
              <div className="font-medium truncate">Puiku! Įdėjote į krepšelį {toast.qty} vnt.</div>
              <div className="text-[#4DD8FF] font-mono text-xs mt-0.5 truncate">{toast.name}</div>
            </div>
          </div>
        </div>
      )}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
